import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { navLinks, LOGO_URL, CONTACT_NUMBERS } from '../mock';
import { useEnrolmentModal } from '../context/EnrolmentModalContext';

const TopBanner = () => {
  const items = [
    'WHITEPLAINS BRITISH SCHOOL \u2014 SCHOOLS IN ABUJA \u2022 NIGERIA \u2022 AFRICA',
    'CAMBRIDGE PATHWAY \u2022 PRE-NURSERY TO A-LEVEL',
    'BONITAS ET DISCIPLINA',
    'ADMISSIONS OPEN FOR 2026 / 2027',
    'WHITEPLAINS BRITISH SCHOOL \u2014 A LEADING INTERNATIONAL SCHOOL IN ABUJA',
    'BONITAS ET DISCIPLINA',
  ];
  const track = [...items, ...items];
  return (
    <div className="relative w-full bg-[#0f0d0b] text-white overflow-hidden border-b border-[#c8942a]/40">
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(90deg, transparent, #c8942a55, transparent)',
      }} />
      <div className="relative flex items-center py-2.5">
        <div className="ticker-x flex gap-16 w-max whitespace-nowrap">
          {track.map((t, i) => (
            <span key={i} className="flex items-center gap-4 font-serif-display text-[13px] md:text-[15px] font-black tracking-[0.28em] uppercase">
              <span className={i % 2 === 0 ? 'badge-shine' : 'text-white'}>{t}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#c8942a]" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const UtilityBar = () => (
  <div className="hidden md:block w-full bg-[#1c1a17] text-[#e6d8c6]/85">
    <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-[12px]">
      <div className="flex items-center gap-4 md:gap-6 overflow-x-auto no-scrollbar">
        <span className="flex items-center gap-2 whitespace-nowrap"><Phone size={13} className="text-[#c8942a]" /> {CONTACT_NUMBERS[0].display}</span>
        <span className="hidden lg:flex items-center gap-2 whitespace-nowrap text-white/60"><Phone size={12} className="text-[#c8942a]/70" /> {CONTACT_NUMBERS[1].display}</span>
        <span className="hidden xl:flex items-center gap-2 whitespace-nowrap text-white/60"><Phone size={12} className="text-[#c8942a]/70" /> {CONTACT_NUMBERS[2].display}</span>
        <span className="flex items-center gap-2 whitespace-nowrap"><Mail size={13} className="text-[#c8942a]" /> admissions@whiteplainsbritishschool.com</span>
      </div>
      <div className="flex items-center gap-5">
        <a href="#contact" className="hover:text-white transition-colors">Parent Portal</a>
        <span className="w-px h-3 bg-white/20" />
        <a href="#contact" className="hover:text-white transition-colors">Careers</a>
        <span className="w-px h-3 bg-white/20" />
        <a href="#contact" className="hover:text-white transition-colors">EN</a>
      </div>
    </div>
  </div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { show } = useEnrolmentModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="sticky top-0 z-50">
      <TopBanner />
      <UtilityBar />
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`w-full transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-[0_10px_40px_-20px_rgba(0,0,0,0.25)]' : 'bg-white'}`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="#home" className="flex items-center gap-4 py-4">
            <motion.img
              whileHover={{ rotate: -6, scale: 1.06 }}
              transition={{ type: 'spring', stiffness: 250 }}
              src={LOGO_URL}
              alt="WBS"
              className="h-14 md:h-16 w-auto"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="leading-tight border-l border-[#c8942a]/40 pl-4">
              <div className="font-serif-display text-[18px] md:text-[22px] font-black tracking-[0.14em] uppercase text-[#1c1a17]">
                Whiteplains British School
              </div>
              <div className="font-cormorant italic text-[13px] md:text-[15px] text-[#8f6417] tracking-wider">
                Bonitas et Disciplina — Goodness &amp; Discipline
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="nav-link-pro text-[#1c1a17] hover:text-[#b8331f]">
                {l.label}
              </a>
            ))}
            <button type="button" onClick={() => show()} className="btn-primary !py-3 !px-5 text-[11px]">Enrol Now</button>
          </nav>

          <button aria-label="menu" onClick={() => setOpen((v) => !v)} className="lg:hidden text-[#1c1a17] p-2">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden bg-white border-t border-[#c8942a]/30"
            >
              <div className="px-6 py-4 flex flex-col gap-2">
                {navLinks.map((l) => (
                  <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="nav-link-pro text-[#1c1a17] py-1">
                    {l.label}
                  </a>
                ))}
                <button type="button" onClick={() => { setOpen(false); show(); }} className="btn-primary self-start mt-3">Enrol Now</button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.header>
    </div>
  );
};

export default Header;
